import React, { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Send, CheckCircle2, Loader2, Building2, Mail, Globe, AlertTriangle } from "lucide-react";

const TYPE_LABELS = {
  general: "General enquiry",
  oem: "OEM partnership",
  custom: "Custom clamp / made-to-drawing",
  hygienic: "Hygienic / food / dairy / brewery",
  dairy: "Dairy industry",
  brewery: "Brewery",
  pharma: "Pharmaceutical",
  semiconductor: "Semiconductor / cleanroom",
  water: "Water treatment",
  hydrogen: "Hydrogen / H₂",
  marine: "Marine / offshore",
  chemical: "Chemical plant",
  hvac: "HVAC",
  visit: "Facility visit",
};

export default function ContactPage() {
  const [params] = useSearchParams();
  const initialType = params.get("type") || "general";
  const [form, setForm] = useState({
    type: initialType,
    name: "", email: "", company: "", phone: "",
    country: "", industry: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const r = await api.post("/contact", form);
      setSuccess(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Unable to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-[700px] flex-col items-center justify-center px-5 py-16 text-center" data-testid="contact-success">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
          <CheckCircle2 size={32} className="text-emerald-600" />
        </div>
        <h2 className="mt-5 font-display text-3xl font-bold">Thank you — we'll be in touch.</h2>
        <p className="mt-3 text-zinc-600">
          Your enquiry has been received. Our team will reply to <strong>{form.email}</strong> within 1 business day.
        </p>
        <p className="mt-2 font-mono text-xs text-zinc-400">
          Reference: {success.id}
        </p>
        <Link
          to="/"
          className="mt-8 rounded-md bg-nibro-sage px-6 py-3 text-sm font-bold text-white hover:bg-nibro-sage-hover"
        >
          Back to homepage
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      <div className="bg-nibro-sage-pale">
        <div className="mx-auto max-w-[1200px] px-5 py-12 lg:px-10">
          <div className="label-uppercase">Contact</div>
          <h1 className="mt-2 font-display text-4xl font-bold tracking-tight text-zinc-900 sm:text-5xl">
            Talk to the factory.
          </h1>
          <p className="mt-3 max-w-2xl text-zinc-600">
            For volume quotes, OEM partnerships, custom clamps or technical questions — our engineering and sales team in Oisterwijk responds within 1 business day.
          </p>
          <div className="mt-4">
            <a
              href="/distributor/register"
              className="inline-flex items-center gap-2 rounded-md border-2 border-nibro-sage-dark bg-white px-4 py-2 text-xs font-bold uppercase tracking-wide text-nibro-sage-dark transition hover:bg-nibro-sage-pale"
              data-testid="contact-distributor-cta"
            >
              Distributeur worden →
            </a>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1200px] px-5 py-10 lg:px-10">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.6fr_1fr]">
          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label>Type of enquiry</Label>
              <select
                value={form.type}
                onChange={(e) => set("type", e.target.value)}
                className="w-full rounded-md border border-zinc-200 bg-white px-3 py-2.5 text-sm focus:border-nibro-sage focus:outline-none focus:ring-2 focus:ring-nibro-sage/30"
                data-testid="contact-type"
              >
                {Object.entries(TYPE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input label="Your name *" value={form.name} onChange={(v) => set("name", v)} required testId="contact-name" />
              <Input label="Email *" type="email" value={form.email} onChange={(v) => set("email", v)} required testId="contact-email" />
              <Input label="Company" value={form.company} onChange={(v) => set("company", v)} testId="contact-company" />
              <Input label="Phone" value={form.phone} onChange={(v) => set("phone", v)} testId="contact-phone" />
              <Input label="Country" value={form.country} onChange={(v) => set("country", v)} testId="contact-country" />
              <Input label="Industry" value={form.industry} onChange={(v) => set("industry", v)} testId="contact-industry" placeholder="e.g. dairy, brewery, pharma" />
            </div>

            <div>
              <Label>Your enquiry / project details *</Label>
              <textarea
                required
                value={form.message}
                onChange={(e) => set("message", e.target.value)}
                rows={6}
                className="w-full rounded-md border border-zinc-200 bg-white px-3 py-2.5 text-sm focus:border-nibro-sage focus:outline-none focus:ring-2 focus:ring-nibro-sage/30"
                placeholder="Volume, sizes (DN/inch/mm), material grade (304/316/Duplex), application, target lead time, certifications required (3.1, EHEDG, FDA), drawing attachments..."
                data-testid="contact-message"
              />
              <p className="mt-1 text-[11px] text-zinc-500">
                For drawings/spec sheets: please reference them in the message — we'll request file upload by email reply.
              </p>
            </div>

            {error && (
              <div className="flex items-start gap-2 rounded-md border border-aramfix-red/40 bg-aramfix-red/5 px-3 py-2 text-xs text-aramfix-red" data-testid="contact-error">
                <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="flex w-full items-center justify-center gap-2 rounded-md bg-nibro-sage px-6 py-3 text-sm font-bold text-white transition hover:bg-nibro-sage-hover disabled:opacity-50 sm:w-auto"
              data-testid="contact-submit"
            >
              {submitting ? <><Loader2 size={14} className="animate-spin" /> Sending...</> : <><Send size={14} /> Send enquiry</>}
            </button>
          </form>

          <aside className="space-y-5">
            <div className="rounded-md border-2 border-nibro-sage bg-nibro-sage-pale p-5">
              <div className="label-uppercase">Direct contact</div>
              <div className="mt-4 space-y-3 text-sm">
                <Row icon={Mail} label="info@aramfix.com" href="mailto:info@aramfix.com" />
                <Row icon={Building2} label="NIBRO BV — Nijverheidsweg, 5061KK Oisterwijk, NL" />
                <Row icon={Globe} label="Worldwide shipping · 22 countries" />
              </div>
            </div>

            <div className="rounded-md border border-zinc-200 bg-white p-5">
              <div className="font-display text-base font-bold">Response time</div>
              <ul className="mt-3 space-y-2 text-sm text-zinc-700">
                <li>· General enquiry: 4 office hours</li>
                <li>· Standard quote: 1 business day</li>
                <li>· OEM partnership: 2 business days</li>
                <li>· Custom engineering: 3-5 business days</li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function Label({ children }) {
  return <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-zinc-600">{children}</label>;
}
function Input({ label, value, onChange, type = "text", required, testId, placeholder }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required}
        placeholder={placeholder}
        className="w-full rounded-md border border-zinc-200 bg-white px-3 py-2.5 text-sm focus:border-nibro-sage focus:outline-none focus:ring-2 focus:ring-nibro-sage/30"
        data-testid={testId}
      />
    </div>
  );
}
function Row({ icon: Icon, label, href }) {
  const inner = (
    <div className="flex items-start gap-2.5">
      <Icon size={14} className="mt-0.5 flex-shrink-0 text-nibro-sage-dark" />
      <span className="text-zinc-800">{label}</span>
    </div>
  );
  return href ? <a href={href} className="block hover:underline">{inner}</a> : inner;
}
